import { useWorkflowStore } from "../../stores/workflowStore.js";
import { StatusEffect } from "../classes/StatusEffect.js";import { Weapon } from "../classes/Weapon.js";import { critLog, damageLog, blockedLog, deathLog, dropLog, healLog, infoLog, statusDamageLog, statusLog } from "../utils/logUtils.js";
import { emit, GameEvents, on } from "./eventBus.js";

function log(gameStore, entry) {
  if (gameStore.isSimulating) return;
  gameStore.fightLog.push(entry);
}

export function initFightEvents(gameStore, inventoryStore) {
  on(GameEvents.CREATURE_HIT, ({ target, damage, attacker, baseDamage, isCrit, critMultiplier, weapon }) => {
    const armor = target.armor || 0;
    const finalDamage = Math.max(0, damage - armor);
    target.health -= finalDamage;
    
    const damageBase = (weapon?.subtype?.mods?.damage || 0) + (weapon?.material?.mods?.damage || 0);
    const damageRoll = Weapon.rollMultiplier(weapon?.rolls?.damage);
    
    const details = {
      baseDamage,
      finalDamage,
      armor,
      isCrit,
      critMultiplier,
      damageBase,
      damageRoll,
    };
    
    if (damage <= armor) {
      log(gameStore, blockedLog(attacker, target, details));
    } else if (isCrit) {
      log(gameStore, critLog(attacker, target, finalDamage, details));
    } else {
      log(gameStore, damageLog(attacker, target, finalDamage, details));
    }
    
    if (target.health <= 0) {
      emit(GameEvents.CREATURE_DEATH, { creature: target });
    }
  });

  on(GameEvents.CREATURE_ATTACK, ({ attacker, defender }) => {
    const weapon = attacker.weapon;
    const baseDamage = weapon.damage;
    let damage = baseDamage;
    let isCrit = false;
    let critMultiplier = weapon.critMultiplier;
    if (weapon.critChance && Math.random() * 100 <= weapon.critChance) {
      damage = damage * critMultiplier;
      isCrit = true;
    }
    emit(GameEvents.CREATURE_HIT, { target: defender, damage, attacker, baseDamage, isCrit, critMultiplier, weapon });

    if (weapon.lifesteal) {
      emit(GameEvents.LIFESTEAL, {
        target: attacker,
        damage,
        lifesteal: weapon.lifesteal,
      });
    }

    if (weapon.statusEffect && Math.random() <= weapon.statusEffect.chance) {
      emit(GameEvents.STATUS_APPLIED, {
        target: defender,
        effect: weapon.statusEffect,
      });
    }
  });

  on(GameEvents.STATUS_APPLIED, ({ target, effect }) => {
    const existing = target.statusEffects.find((e) => e.type === effect.type);
    if (existing) {
      existing.addStacks(effect.stacks);
      log(gameStore, statusLog(target, effect.name, existing.stacks, false));
    } else {
      const newEffect = new StatusEffect(effect);
      target.statusEffects.push(newEffect);
      log(gameStore, statusLog(target, effect.name, effect.stacks, true));
    }
  });

  on(GameEvents.LIFESTEAL, ({ target, damage, lifesteal }) => {
    const amount = damage * lifesteal / 100;
    const actualHeal = Math.min(amount, target.maxHealth - target.health);
    if (actualHeal <= 0) return;
    target.health += actualHeal;
    log(gameStore, healLog(target, actualHeal, { source: "lifesteal", lifestealPercent: lifesteal, damageDealt: damage }));
  });

  on(GameEvents.STATUS_EFFECT_TICK, ({ target }) => {
    for (const effect of target.statusEffects) {
      if (effect.isTurnBased) continue;
      const damage = effect.tick();
      if (damage > 0) {
        emit(GameEvents.STATUS_DAMAGE, { target, damage, effect });
      }
    }
    target.statusEffects = target.statusEffects.filter((e) => e.stacks > 0);
  });

  on(GameEvents.STATUS_DAMAGE, ({ target, damage, effect }) => {
    target.health -= damage;
    log(gameStore, statusDamageLog(target, damage, effect));
    if (target.health <= 0) {
      emit(GameEvents.CREATURE_DEATH, { creature: target });
    }
  });

  on(GameEvents.STUN_SKIP, ({ target }) => {
    log(gameStore, infoLog(`${target.name} is stunned and skips their turn!`));
  });

  on(GameEvents.CREATURE_DEATH, ({ creature }) => {
    log(gameStore, deathLog(creature));
    if (gameStore.isSimulating) return;
    gameStore.stopFight();
    if (creature.isPlayer) {
      gameStore.respawnPlayer();
    } else {
      emit(GameEvents.ITEM_DROP, {
        item: gameStore.enemy.weapon,
        type: "weapon",
      });
      processDrops(gameStore);
      gameStore.startFight();
    }
  });

  on(GameEvents.ITEM_DROP, ({ item, type }) => {
    if (type === "material") {
      inventoryStore.addMaterial(item);
      log(gameStore, dropLog(item));
    } else {
      emit(GameEvents.ITEM_ADDED, { item });
      log(gameStore, dropLog(item));
      useWorkflowStore().processWorkflows("onDrop", item);
    }
  });

  on(GameEvents.ITEM_ADDED, ({ item }) => {
    inventoryStore.addItem(item);
  });
}

function processDrops(gameStore) {
  const monster = gameStore.currentMonster;
  if (!monster.drops) return;

  for (const drop of monster.drops) {
    if (Math.random() > drop.chance) continue;

    if (drop.type === "material") {
      emit(GameEvents.ITEM_DROP, {
        item: drop.material.name,
        type: "material",
      });
    }
  }
}
